import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { useTheme, ADULT_THEMES } from "../context/ThemeContext";
import { Star, ListMusic, Music2, Piano, Award, CalendarDays } from "lucide-react";

function StarBadge({ value, label }) {
  return (
    <div className="relative inline-flex flex-col items-center" data-testid="star-badge">
      <svg viewBox="0 0 120 120" className="w-36 h-36 drop-shadow-xl animate-float">
        <defs>
          <linearGradient id="star-grad" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#FFD166" />
            <stop offset="100%" stopColor="#FFB703" />
          </linearGradient>
        </defs>
        <polygon points="60,6 73,45 114,45 80,70 93,110 60,84 27,110 40,70 6,45 47,45" fill="url(#star-grad)" stroke="#E09A00" strokeWidth="2.5" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display font-black text-4xl text-[#5a3d00]">{value}</div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-[#5a3d00]">{label}</div>
      </div>
    </div>
  );
}

/** Adult-theme equivalent of StarBadge: minimalist ring, no cartoon. */
function RingBadge({ value, label }) {
  const r = 52;
  const c = 2 * Math.PI * r;
  const pct = Math.min(value / 30, 1); // 30-day ring scale
  return (
    <div className="relative inline-flex flex-col items-center" data-testid="star-badge">
      <svg viewBox="0 0 120 120" className="w-36 h-36">
        <circle cx="60" cy="60" r={r} fill="none" stroke="var(--app-border)" strokeWidth="6" />
        <circle cx="60" cy="60" r={r} fill="none" stroke="var(--app-primary)" strokeWidth="6"
                strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
                strokeLinecap="round" transform="rotate(-90 60 60)" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display font-bold text-4xl" style={{ color: "var(--app-text)" }}>{value}</div>
        <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--app-text-muted)" }}>{label}</div>
      </div>
    </div>
  );
}

function CartoonPianoGuy() {
  return (
    <svg viewBox="0 0 160 160" className="w-40 h-40" aria-hidden>
      <g>
        <rect x="10" y="90" width="140" height="40" rx="8" fill="#0a0a0a" />
        <rect x="14" y="116" width="132" height="12" fill="#fff" />
        {[0,1,2,3,4,5,6,7,8,9].map((i) => (
          <rect key={i} x={14 + i*13.2} y="116" width="13.2" height="12" fill="#fff" stroke="#0a0a0a" strokeWidth="0.6" />
        ))}
        {[0,1,3,4,5,7,8].map((i) => (
          <rect key={`b${i}`} x={22.6 + i*13.2} y="116" width="6.6" height="7" fill="#0a0a0a" />
        ))}
        {/* face */}
        <circle cx="80" cy="58" r="32" fill="#fff1cc" stroke="#0a0a0a" strokeWidth="2" />
        <circle cx="70" cy="58" r="3.4" fill="#0a0a0a" />
        <circle cx="90" cy="58" r="3.4" fill="#0a0a0a" />
        <path d="M68 70 Q80 80 92 70" stroke="#0a0a0a" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        {/* cheeks */}
        <circle cx="62" cy="66" r="3" fill="#ff9aa8" />
        <circle cx="98" cy="66" r="3" fill="#ff9aa8" />
        {/* music note on hat */}
        <rect x="60" y="20" width="40" height="14" rx="2" fill="#0a0a0a" />
        <rect x="56" y="30" width="48" height="4" fill="#0a0a0a" />
        <rect x="56" y="30" width="48" height="2" fill="#FFB703" />
      </g>
    </svg>
  );
}

/** Adult-theme hero illustration: a real, anatomically-correct treble clef on a staff.
 *  SVG path is hand-tuned from the standard music-notation glyph so it renders the
 *  same on every device (no font dependency). currentColor → picks up theme tint. */
function AdultMusicianMark() {
  return (
    <svg viewBox="0 0 200 200" className="w-32 h-32 md:w-36 md:h-36 shrink-0" aria-hidden style={{ color: "rgba(255,255,255,0.95)" }}>
      {/* Five-line staff backdrop */}
      <g stroke="currentColor" strokeWidth="1.2" opacity="0.30">
        <line x1="20" y1="70"  x2="180" y2="70" />
        <line x1="20" y1="86"  x2="180" y2="86" />
        <line x1="20" y1="102" x2="180" y2="102" />
        <line x1="20" y1="118" x2="180" y2="118" />
        <line x1="20" y1="134" x2="180" y2="134" />
      </g>
      {/* Treble clef — real path, single fill, no font fallback needed */}
      <path
        fill="currentColor"
        d="M101.5 18c-3 0-6.5 1.5-8.5 5-2 3-3 7-3 11.5 0 5 1 9.5 2.5 14.5l3 9.5c-13 6-22 18-22 33 0 12 6 22 15 28 4 3 9 4.5 14 4.5l3.5 16c1 4.5 1 8-1 10.5-1.5 2-4 3-7 3-4 0-7-2-8.5-5-1-2-1.5-4.5-1-7 .5-2 2-3.5 4-3.5 1 0 2 .5 2.5 1.5.5 1 .5 2 0 3-1 1.5-3 2-3 3.5 0 2.5 3 4 6 4 5 0 9-4 9-9.5 0-7-5-12-12-12-6 0-11 4.5-11 11.5 0 8 7 14 16 14 5.5 0 10-1.5 13-5 3-3.5 4-8 3-13l-3.5-17c5.5-1.5 10-4.5 13-9 3.5-5 5-11 4-17.5-1.5-9-7.5-15.5-16-17.5 0 0-2-6.5-2.5-8.5-2.5-11.5-2-21 4-21 4 0 6.5 4 7 9 .5 4.5 0 9.5-2 13.5-1 2-2.5 3.5-4 4.5 2 .5 4 0 5.5-1 4.5-3.5 7.5-9 8-15 .5-7-2-13.5-7-17.5-2-1.5-4.5-2-7-2zM93 86c5 0 10 1.5 13 5 3.5 4 5 9.5 4 15-1 6.5-5 11-11 12.5l-3.5-16c-1-4 0-7 2-9 1-1 2.5-1.5 4-1.5 1.5 0 3 .5 4 1.5 1 1 1.5 2 1.5 3 0 1.5-1 3-2.5 3.5-.5 0-1-.5-.5-1 .5-.5 1-1 1-1.5 0-1-1-2-2.5-2-2 0-3.5 2-3.5 4.5 0 3 2.5 5.5 6 5.5 4 0 7-3 7-7 0-5-4.5-9-10-9-3 0-5.5 1-7.5 3-3.5 3-5 8-4 13 1 5 4.5 9 9 10.5l4 18c-7-1-12.5-5.5-15-12.5-2.5-7.5 0-15 7-19 1-.5 1.5-1 1.5-1 0 0-2 .5-3 1z"
      />
    </svg>
  );
}

export default function StudentDashboard() {
  const { user } = useAuth();
  const t = useT();
  const { theme } = useTheme();
  const isAdult = ADULT_THEMES.includes(theme);
  const [phrases, setPhrases] = useState([]);
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [streaks, setStreaks] = useState(null);
  const [songs, setSongs] = useState([]);
  const [recital, setRecital] = useState(null);

  useEffect(() => {
    apiClient.get("/greeting-phrases").then((r) => {
      setPhrases(r.data.phrases);
      setPhraseIdx(Math.floor(Math.random() * Math.max(1, r.data.phrases.length)));
    });
    if (user?.student_id) {
      apiClient.get(`/practice/streaks/${user.student_id}`).then((r) => setStreaks(r.data));
    }
    apiClient.get("/songs").then((r) => setSongs(r.data.songs));
    apiClient.get("/recitals").then((r) => {
      const upcoming = (r.data.recitals || []).sort((a, b) => a.date.localeCompare(b.date))[0];
      setRecital(upcoming);
    });
    const t = setInterval(() => setPhraseIdx((i) => (i + 1) % Math.max(1, phrasesRef.current.length)), 6000);
    return () => clearInterval(t);
  }, [user?.student_id]);

  const phrasesRef = React.useRef([]);
  React.useEffect(() => { phrasesRef.current = phrases; }, [phrases]);

  const currentPhrase = phrases[phraseIdx] || { phrase: "You've got this!" };

  return (
    <div className="space-y-8" data-testid="student-dashboard">
      {/* Hero — youth themes get the full kids+piano illustration as the entire hero card.
          Adult themes keep the gradient + dynamic motivational copy. */}
      {isAdult ? (
        <div className="rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 relative overflow-hidden"
             style={{ background: "linear-gradient(135deg, var(--app-primary), var(--app-secondary))", color: "#fff" }}>
          <div className="staff-lines absolute inset-0 opacity-20" />
          <AdultMusicianMark />
          <div className="relative z-10 flex-1">
            <div className="text-xs uppercase font-bold tracking-widest opacity-90">{t("Hello")}, {user?.name?.split(" ")[0]}!</div>
            <div className="font-play text-4xl md:text-5xl leading-tight mt-1" data-testid="motivational-phrase">"{currentPhrase.phrase}"</div>
            <div className="mt-3 flex gap-3">
              <Link to="/student/calendar" className="btn-pill btn-accent text-sm">{t("Log practice")}</Link>
              <Link to="/student/songs" className="btn-pill text-sm" style={{ background: "rgba(255,255,255,0.2)", color: "#fff" }}>{t("Songs I'm learning")}</Link>
            </div>
          </div>
        </div>
      ) : (
        <div className="rounded-3xl overflow-hidden relative" data-testid="student-hero">
          <img
            src="/student-hero.png"
            alt={t("Today's practice is tomorrow's recital.")}
            className="w-full h-auto block"
            data-testid="student-hero-illustration"
          />
          {/* Click-through hotspots: invisible overlays on top of the illustration's
              baked-in "Log practice" and "Songs I'm learning" buttons so taps actually navigate. */}
          <Link
            to="/student/calendar"
            data-testid="hero-cta-log-practice"
            aria-label={t("Log practice")}
            className="absolute"
            style={{ left: "6%", top: "72%", width: "22%", height: "12%" }}
          />
          <Link
            to="/student/songs"
            data-testid="hero-cta-songs"
            aria-label={t("Songs I'm learning")}
            className="absolute"
            style={{ left: "6%", top: "86%", width: "28%", height: "12%" }}
          />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="bento-card p-6 lg:col-span-1 flex flex-col items-center text-center">
          <div className="text-xs uppercase font-bold tracking-widest" style={{ color: "var(--app-text-muted)" }}>{t("My Streaks")}</div>
          {isAdult
            ? <RingBadge value={streaks?.daily_streak ?? 0} label={t("day streak")} />
            : <StarBadge value={streaks?.daily_streak ?? 0} label={t("day streak")} />}
          <div className="mt-4 grid grid-cols-2 gap-3 w-full">
            <div className="p-3 rounded-xl" style={{ background: "var(--app-surface-2)" }}>
              <div className="text-[10px] uppercase font-bold" style={{ color: "var(--app-text-muted)" }}>{t("Full session")}</div>
              <div className="font-display text-2xl font-bold">{streaks?.full_session_streak ?? 0}</div>
            </div>
            <div className="p-3 rounded-xl" style={{ background: "var(--app-surface-2)" }}>
              <div className="text-[10px] uppercase font-bold" style={{ color: "var(--app-text-muted)" }}>{t("Total min")}</div>
              <div className="font-display text-2xl font-bold">{streaks?.total_minutes ?? 0}</div>
            </div>
          </div>
        </div>

        <Link to="/student/songs" className="bento-card p-6 group" data-testid="card-songs">
          <div className="flex items-center gap-3"><ListMusic className="w-6 h-6" /><h2 className="font-display font-bold text-xl">{t("Songs I'm Learning")}</h2></div>
          <div className="font-display text-5xl font-bold mt-3">{songs.length}</div>
          <p className="text-sm" style={{ color: "var(--app-text-muted)" }}>{t("Tap to see your pieces and status.")}</p>
        </Link>

        <Link to="/student/scales" className="bento-card p-6 group" data-testid="card-scales">
          <div className="flex items-center gap-3"><Music2 className="w-6 h-6" /><h2 className="font-display font-bold text-xl">{t("My Scales")}</h2></div>
          <div className="mt-3 font-play text-3xl">C D E F G A B</div>
          <p className="text-sm mt-1" style={{ color: "var(--app-text-muted)" }}>{t("12 major scales, one happy keyboard.")}</p>
        </Link>

        <Link to="/student/calendar" className="bento-card p-6" data-testid="card-calendar">
          <div className="flex items-center gap-3"><CalendarDays className="w-6 h-6" /><h2 className="font-display font-bold text-xl">{t("Calendar")}</h2></div>
          <p className="text-sm mt-2" style={{ color: "var(--app-text-muted)" }}>{t("See your lessons and log practice here.")}</p>
        </Link>

        {!user?.solo_learner && (
          <div className="bento-card p-6" data-testid="card-recital">
            <div className="flex items-center gap-3"><Award className="w-6 h-6" /><h2 className="font-display font-bold text-xl">{t("Next Recital")}</h2></div>
            {recital ? (
              <div className="mt-2">
                <div className="font-display font-bold text-lg">🎤 {recital.title}</div>
                <div className="text-sm" style={{ color: "var(--app-text-muted)" }}>{recital.date} {t("at")} {recital.time}</div>
                {recital.location && <div className="text-sm">{recital.location}</div>}
              </div>
            ) : <p className="text-sm mt-2" style={{ color: "var(--app-text-muted)" }}>{t("No recital scheduled yet.")}</p>}
          </div>
        )}

        <Link to="/student/my-piano" className="bento-card p-6" data-testid="card-piano">
          <div className="flex items-center gap-3"><Piano className="w-6 h-6" /><h2 className="font-display font-bold text-xl">{t("My Instrument")}</h2></div>
          <p className="text-sm mt-2" style={{ color: "var(--app-text-muted)" }}>{t("Show off your instrument (and your dream one).")}</p>
        </Link>
      </div>
    </div>
  );
}
